"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Waves, Users, House, UsersRound as Users2 } from 'lucide-react';
import SectionContainer from "@/components/common/SectionContainer";
const benefitsList = [{
  id: "benefit-1",
  icon: "waves",
  title: "Lakeside Setting",
  description: "Waterfront views set a naturally elegant backdrop for your ceremony."
}, {
  id: "benefit-3",
  icon: "house",
  title: "Indoor & Outdoor Flexibility",
  description: "Ceremony and reception spaces that adapt to your vision and the weather."
}, {
  id: "benefit-4",
  icon: "users2",
  title: "Flexible Packages",
  description: "Accommodating up to 200 seated or 300 standing guests."
}];
function BenefitIcon({
  name
}: {
  name: string;
}) {
  const iconProps = {
    className: "size-6 stroke-[1.25] text-foreground transition-colors duration-300 group-hover:text-primary",
    "aria-hidden": true
  };
  switch (name) {
    case "waves":
      return <Waves {...iconProps} />;
    case "users":
      return <Users {...iconProps} />;
    case "house":
      return <House {...iconProps} />;
    case "users2":
      return <Users2 {...iconProps} />;
    default:
      return <Waves {...iconProps} />;
  }
}
export default function Benefits() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.1
      }
    }
  };
  const itemVariants = {
    hidden: shouldReduceMotion ? {
      opacity: 1,
      y: 0
    } : {
      opacity: 0,
      y: 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };
  return <section id="corebenefits" data-nav="light" className="relative bg-background text-foreground py-10 md:py-14 lg:py-16">
      <SectionContainer>
        {/* Header Block */}
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.3
      }} transition={{
        duration: 0.8,
        ease: [0.22, 1, 0.36, 1]
      }} className="max-w-3xl mb-8 sm:mb-10 lg:mb-12">
          <p className="text-xs uppercase tracking-[0.3em] font-semibold text-muted-foreground mb-4">
            Why Choose Us
          </p>
          <h2 className="font-default text-2xl sm:text-3xl lg:text-4xl font-normal tracking-tight text-foreground leading-[1.15]">
            Thoughtfully designed for effortless gatherings and lakefront memories.
          </h2>
        </motion.div>

        {/* Benefits Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.2
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
          {benefitsList.map((item, i) => <motion.div key={item.id} data-index={i} variants={itemVariants} className="group relative flex flex-col p-6 rounded-lg border border-border/70 bg-card/40 transition-[border-color,background-color] duration-300 hover:border-foreground/80 hover:bg-card">
              <div className="flex items-center gap-3 mb-3">
                <div className="shrink-0 inline-flex p-2.5 rounded-md bg-accent/60 transition-colors duration-300 group-hover:bg-accent">
                  <BenefitIcon name={item.icon} />
                </div>
                <h3 className="font-default text-lg sm:text-xl font-normal text-foreground tracking-tight">
                  {item.title}
                </h3>
              </div>

              <p className="font-sans text-sm sm:text-base text-muted-foreground leading-relaxed">
                {item.description}
              </p>
            </motion.div>)}
        </motion.div>
      </SectionContainer>
    </section>;
}